import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import Autocomplete from 'components/Autocomplete'
import Html from 'components/Html'
import Icon from 'components/Icon'
import { useEventContext } from 'context/EventContext'
import debounce from 'helpers/debounce'
import openUrl from 'helpers/openUrl'
import { suggestionRequestObject } from 'helpers/suggestionRequestObject'
import useViewport from 'hooks/useViewport'

import matchesCaseInsensitive from './matchesCaseInsensitive'

import styles from './Search.module.scss'

export const parseResults = (query, results) => {
  if (results.length === 0) {
    return [
      {
        isEmpty: true,
      },
    ]
  }

  results.sort((resultA, resultB) => Number(resultA.best_match) - Number(resultB.best_match))

  const bestMatch = results.find(result => result.best_match)

  const { listings, categories } = results.reduce(
    (acc, result) => {
      return {
        listings:
          result.entity_type === 'listing'
            ? [
                {
                  ...result,
                  type: 'apps',
                  name: result.name,
                },
                ...acc.listings,
              ]
            : acc.listings,
        categories:
          result.entity_type === 'category'
            ? [{ ...result, type: 'categories', name: result.name }].concat(acc.categories)
            : acc.categories,
      }
    },
    {
      listings: [
        {
          href: `/s/${encodeURIComponent(query)}/`,
          type: 'apps',
          allResults: true,
        },
      ],
      categories: [],
    }
  )
  if (bestMatch.entity_type === 'category') {
    return categories.concat(listings)
  }
  return listings.concat(categories)
}

/** @type {import('./types').default} */
const Search = props => {
  const { addEvent, sendEvent } = useEventContext()
  const { up } = useViewport()
  const [options, setOptions] = useState([])
  const [query, setQuery] = useState('')
  const regex = new RegExp(`(${query.trim()})`, 'gi')
  const keptRef = useRef()
  const SUGGESTIONS_EMPTY = []
  const {
    onBlur,
    focusOnDisplay,
    colorVariant,
    placeholderCentered,
    truncatePlaceholderMobile = true,
    testId,
    size = 'small',
    ...otherProps
  } = props

  useEffect(() => {
    if (focusOnDisplay && keptRef.current?.current) {
      keptRef.current?.current.focus()
    }
  }, [focusOnDisplay])

  const onClear = () => {
    setOptions([])
  }

  const fetchSuggestions = value => {
    const queryParams = new URLSearchParams({ q: value.trim() })
    fetch(`/apiui/ac/?${queryParams}`)
      .then(response => response.json())
      .then(data => {
        data = data || []
        const results = data.filter(x => x.entity_type !== 'fullSearch')
        setOptions(parseResults(value.trim(), results))
      })
  }

  const onChangeText = value => {
    const suggestionRequest = suggestionRequestObject(value)
    if (suggestionRequest.isValid) {
      suggestionRequest.isInitialSearch &&
        sendEvent({
          name: 'search',
          searchTerm: suggestionRequest.value,
          searchCount: 1,
          engagementComponent: 'search-engine_serp',
          engagementDestination: 'start_search',
          engagementDetails: 'search-bar',
        })
      setQuery(suggestionRequest.escapedValue)
      fetchSuggestions(suggestionRequest.value)

      return
    }

    setOptions(SUGGESTIONS_EMPTY)
  }

  const onSubmit = (_, value) => {
    sendEvent({
      engagementComponent: 'search-engine',
      engagementDestination: 'search_enter-search',
      engagementDetails: 'search-bar',
      name: 'engagement_search',
    })

    if (!value || !options.length || options[0].isEmpty) {
      return
    }

    const matchingOption = options
      .filter(x => x?.name)
      .find(option => matchesCaseInsensitive(value.trim(), option.name))

    if (matchingOption) openUrl(matchingOption.href, { rel: null, target: '_self' })
    else
      openUrl(`/s/${encodeURIComponent(value)}/`, {
        rel: null,
        target: '_self',
      })
  }

  return (
    <Autocomplete
      onClear={onClear}
      className="Search"
      fullWidth
      componentsClasses={{
        paper: styles.paper,
        listbox: styles.listbox,
        option: styles.option,
        popperDisablePortal: styles.popperDisablePortal,
      }}
      {...otherProps}
      onChange={debounce(onChangeText, 300)}
      onFocus={() =>
        sendEvent({
          engagementComponent: 'search-engine',
          engagementDestination: 'search_select-search-bar',
          engagementDetails: 'search-bar',
          name: 'engagement_search_bar',
        })
      }
      options={options}
      groupBy={option => option.type}
      getLabel={option => option.name}
      renderOption={option => {
        if (option.isEmpty) {
          return (
            <div className="Search__NoResults" data-testid="app_bar-search_no_results">
              <div className={styles.noResultsTitle}>Sorry, we couldn’t find any matches.</div>
              <div className={styles.noResultsText}>
                Make sure your keywords are spelled correctly or try another search.
              </div>
            </div>
          )
        }
        if (option.allResults) {
          return (
            <a
              className={clsx('Search__AllResults', styles.item, styles.allResults)}
              href={option.href}
              {...addEvent({
                engagementComponent: 'search-engine_serp',
                engagementDestination: 'go-to_serp',
                engagementDetails: 'button_see-all-results',
                name: 'select_search_result',
              })}
              data-testid="app_bar-search_see_all_results"
            >
              SEE ALL RESULTS
            </a>
          )
        }
        if (option.name) {
          return (
            <a
              className={clsx('Search__Item', styles.item, {
                [styles.allResults]: option.allResults,
              })}
              href={option.href}
              {...addEvent(
                option.type !== 'categories'
                  ? {
                      engagementComponent: 'search-engine_serp',
                      engagementDestination: 'go-to_product_details',
                      engagementDetails: 'serp-item_product',
                      name: 'select_search_result',
                      searchTerm: query,
                      searchResultsSelected: 1,
                    }
                  : {
                      engagementComponent: 'search-engine_serp',
                      engagementDestination: 'go-to_category_all-products',
                      engagementDetails: 'serp-item_category',
                      name: 'select_search_result',
                      searchTerm: query,
                      searchResultsSelected: 1,
                    }
              )}
            >
              <span className={styles.iconContainer}>
                {option.img && <img src={option.img} className={styles.icon} alt="logo" />}
                {option.type === 'categories' && (
                  <Icon size={24} icon={['ga', 'category']} className={styles.icon} alt={option.name} />
                )}
              </span>

              <Html>{option.name.replace(regex, '<b>$1</b>')}</Html>
            </a>
          )
        }
      }}
      renderGroup={params => {
        return (
          <>
            <div className={styles.group}>
              {params.group}
              <div className={styles.divider} />
            </div>
            {params.children}
          </>
        )
      }}
      inputProps={{
        placeholder: !up('md') && truncatePlaceholderMobile ? 'Search apps' : 'Search apps, categories, reviews...',
        placeholderCentered,
        colorVariant,
        testId,
        name: 'search',
        size,
      }}
      onBlur={onBlur}
      onSubmit={onSubmit}
      getOptionDisabled={option => option.isEmpty}
    />
  )
}

export default Search
